import { memo, useState, useCallback, useEffect } from 'react';
import { Handle, Position } from 'reactflow';
import { Gem, X, Loader2, Play, Download, Eye, RefreshCw } from 'lucide-react';
import { downloadFile } from '../../utils/downloadFile';
import { useWorkflowStore } from '../../store/workflowStore';
import { useNodeExecution } from '../../hooks/useNodeExecution';
import { useWorkflowTrigger } from '../../hooks/useWorkflowTrigger';
import NodeError from './NodeError';
import NodeResetButton from './NodeResetButton';
import {
  ReconfigureButton, ReconfigureNotice, useReconfigure,
} from './ReconfigureControls';
import { useMediaPreview } from '../../hooks/useMediaPreview';
import SendToTimelineButton from '../SendToTimelineButton';
import DirectorsTrigger from '../studio/DirectorsTrigger';
import PromptField from '../studio/PromptField';
import NodeRequirements from './NodeRequirements';
import { missingOf, requirementsTitle, textNeed } from '../../utils/nodeRequirements';

const ASPECT_RATIOS = [
  { value: '1:1', label: '1:1' },
  { value: '3:4', label: '3:4' },
  { value: '4:3', label: '4:3' },
  { value: '9:16', label: '9:16' },
  { value: '16:9', label: '16:9' },
];

const IMAGE_SIZES = [
  { value: '1K', label: '1K' },
  { value: '2K', label: '2K (máx.)' },
];

const SAFETY_LEVELS = [
  { value: 'block_only_high', label: 'Permisivo' },
  { value: 'block_medium_and_above', label: 'Moderado' },
  { value: 'block_low_and_above', label: 'Estricto' },
];

const OUTPUT_FORMATS = [
  { value: 'jpg', label: 'JPG' },
  { value: 'png', label: 'PNG' },
];

const Image4UltraNode = memo(({ id, data, selected }) => {
  const [chronometer, setChronometer] = useState(0);
  const [prompt, setPrompt] = useState(data.prompt || '');
  const [aspectRatio, setAspectRatio] = useState(data.aspectRatio || '1:1');
  const [imageSize, setImageSize] = useState(data.imageSize || '1K');
  const [safetyFilterLevel, setSafetyFilterLevel] = useState(data.safetyFilterLevel || 'block_only_high');
  const [outputFormat, setOutputFormat] = useState(data.outputFormat || 'jpg');
  const [lastPrompt, setLastPrompt] = useState(data.lastPrompt || '');

  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);
  const deleteNode = useWorkflowStore((state) => state.deleteNode);
  const addAsset = useWorkflowStore((state) => state.addAsset);

  const { processNode, status: execStatus, error: execError, reset: resetExec, insufficientCredits } = useNodeExecution();
  const { openImagePreview } = useMediaPreview();

  const isProcessing = execStatus === 'uploading' || execStatus === 'queued' || execStatus === 'processing';

  useEffect(() => {
    updateNodeData(id, { prompt, aspectRatio, imageSize, safetyFilterLevel, outputFormat });
  }, [prompt, aspectRatio, imageSize, safetyFilterLevel, outputFormat]); // eslint-disable-line

  useEffect(() => {
    let interval;
    if (isProcessing) {
      setChronometer(0);
      interval = setInterval(() => setChronometer((prev) => prev + 1), 1000);
    }
    return () => { if (interval) clearInterval(interval); };
  }, [isProcessing]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Regenerar descarta el resultado; volver a configurar lo conserva.
  const { reconfigure, setReconfigure, showForm } = useReconfigure(data);

  const handleProcess = useCallback(async () => {
    if (!prompt.trim()) return false;
    resetExec();

    const usedPrompt = prompt.trim();

    const result = await processNode({
      resultType: 'imagen4_ultra',
      files: [],
      params: {
        prompt: usedPrompt,
        aspect_ratio: aspectRatio,
        image_size: imageSize,
        safety_filter_level: safetyFilterLevel,
        output_format: outputFormat,
      },
    });

    if (result?.outputUrl) {
      setLastPrompt(usedPrompt);
      setReconfigure(false);
      updateNodeData(id, {
        hasOutput: true,
        outputUrl: result.outputUrl,
        assetId: result.assetId ?? null,
        lastPrompt: usedPrompt,
        processedAt: new Date().toISOString(),
      });
      addAsset({
        id: `asset_${Date.now()}`,
        type: 'image',
        tool: 'imagen4Ultra',
        filename: `imagen4ultra_${Date.now()}.${outputFormat || 'jpg'}`,
        url: result.outputUrl,
        prompt: usedPrompt,
        createdAt: new Date().toISOString(),
      });
      return true;
    }
    return false;
  }, [id, prompt, aspectRatio, imageSize, safetyFilterLevel, outputFormat, updateNodeData, addAsset, processNode, resetExec, setReconfigure]);

  const handleRegenerate = useCallback(() => {
    updateNodeData(id, { hasOutput: false, outputUrl: null });
    resetExec();
  }, [id, updateNodeData, resetExec]);

  const { isInQueue, isMyTurn } = useWorkflowTrigger(id, handleProcess);

  const canProcess = prompt.trim().length > 0;

  // NR-1 · qué le falta al nodo para generar (se muestra debajo del botón)
  const runMissing = missingOf([textNeed(prompt)]);

  return (
    <div className={`node-container imagen4Ultra-node ${selected ? 'selected' : ''} ${isInQueue ? 'workflow-queued' : ''} ${isMyTurn ? 'workflow-active' : ''}`}>
      <NodeResetButton nodeId={id} hasOutput={!!data.hasOutput} />

      {/* Header */}
      <div className="node-header imagen4Ultra-header">
        <div className="node-header-left">
          <Gem size={16} />
          <span>Image - 4Ultra</span>
        </div>
        <button className="node-delete-btn" onClick={() => deleteNode(id)} data-testid="delete-imagen4ultra">
          <X size={14} />
        </button>
      </div>

      {/* Content */}
      <div className="node-content imagen4ultra-content">
        {isProcessing ? (
          <div className="node-processing">
            <div className="node-chronometer">{formatTime(chronometer)}</div>
            <div className="node-processing-info">
              <Loader2 size={16} className="animate-spin" />
              <span>
                {execStatus === 'uploading' ? 'Subiendo archivos...' :
                 execStatus === 'queued' ? 'En cola...' :
                 'Generando imagen ultra...'}
              </span>
            </div>
          </div>
        ) : !showForm ? (
          /* ===== RESULT VIEW ===== */
          <div className="node-result imagen4ultra-result-view">
            <img
              src={data.outputUrl}
              alt="Image - 4Ultra"
              className="node-result-image clickable"
              onClick={() => openImagePreview(data.outputUrl, 'Image - 4Ultra')}
            />
            <div className="node-result-actions">
              <button className="node-mini-preview-btn" onClick={() => openImagePreview(data.outputUrl, 'Image - 4Ultra')} data-testid="imagen4ultra-preview-btn">
                <Eye size={12} /> Ver
              </button>
              <button className="node-mini-download-btn" onClick={(e) => { e.stopPropagation(); downloadFile(data.outputUrl, `imagen4ultra_${Date.now()}.${outputFormat || 'jpg'}`); }} data-testid="imagen4ultra-download-btn">
                <Download size={12} /> Descargar
              </button>
              <SendToTimelineButton assetId={data.assetId} nodeId={id} url={data.outputUrl} kind="image" label="imagen4ultra" testIdPrefix="imagen4ultra-timeline" />
            </div>

            <div className="nano-regen-section">
              <span className="nano-section-label">Prompt usado:</span>
              <PromptField
                value={prompt}
                onChange={setPrompt}
                nodeId={id}
                nodeType="imagen4Ultra"
                label="Prompt de regeneración"
                className="nano-prompt-textarea nano-regen-textarea"
                rows={2}
                testId="imagen4ultra-regen-prompt"
              />
              <button
                className="nano-regen-btn imagen4Ultra-regen-btn"
                onClick={handleRegenerate}
                data-testid="imagen4ultra-regen-btn"
              >
                <RefreshCw size={12} />
                Regenerar
              </button>
              <ReconfigureButton
                onClick={() => setReconfigure(true)}
                testId="imagen4ultra-reconfigure-btn"
              />
            </div>
          </div>
        ) : (
          <>
            {data.outputUrl && reconfigure && (
              <ReconfigureNotice
                previewUrl={data.outputUrl}
                onCancel={() => setReconfigure(false)}
                testId="imagen4ultra-reconfigure-notice"
              />
            )}

            {/* Prompt */}
            <div className="nano-prompt-section">
              <div className="nano-prompt-header">
                <span className="nano-section-label">Prompt *</span>
                <DirectorsTrigger nodeId={id} nodeType="imagen4Ultra" currentPrompt={prompt} onInsert={setPrompt} />
              </div>
              <PromptField
                value={prompt}
                onChange={setPrompt}
                nodeId={id}
                nodeType="imagen4Ultra"
                placeholder="Describe la imagen en detalle: sujeto, estilo, iluminación, texto a renderizar..."
                className="nano-prompt-textarea"
                rows={3}
                testId="imagen4ultra-prompt"
              />
            </div>

            {/* Options: ratio, tamaño, seguridad, formato */}
            <div className="nano-options-row imagen4Ultra-options">
              <div className="nano-option">
                <span className="nano-option-label">Ratio</span>
                <select value={aspectRatio} onChange={(e) => setAspectRatio(e.target.value)} className="nano-select" data-testid="imagen4ultra-aspect-ratio">
                  {ASPECT_RATIOS.map((ar) => (
                    <option key={ar.value} value={ar.value}>{ar.label}</option>
                  ))}
                </select>
              </div>
              <div className="nano-option">
                <span className="nano-option-label">Tamaño</span>
                <select value={imageSize} onChange={(e) => setImageSize(e.target.value)} className="nano-select" data-testid="imagen4ultra-image-size">
                  {IMAGE_SIZES.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div className="nano-option">
                <span className="nano-option-label">Seguridad</span>
                <select value={safetyFilterLevel} onChange={(e) => setSafetyFilterLevel(e.target.value)} className="nano-select" data-testid="imagen4ultra-safety-level">
                  {SAFETY_LEVELS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div className="nano-option">
                <span className="nano-option-label">Formato</span>
                <select value={outputFormat} onChange={(e) => setOutputFormat(e.target.value)} className="nano-select" data-testid="imagen4ultra-output-format">
                  {OUTPUT_FORMATS.map((of_) => (
                    <option key={of_.value} value={of_.value}>{of_.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <button
              className="node-process-btn imagen4Ultra-process-btn"
              onClick={handleProcess}
              disabled={!canProcess}
              title={requirementsTitle(runMissing)}
              data-testid="imagen4ultra-process-btn"
            >
              <Play size={14} />
              {data.outputUrl && reconfigure ? 'Regenerar con cambios' : 'Generar en Ultra'}
            </button>
            <NodeRequirements missing={runMissing} />
          </>
        )}

        <NodeError error={execError} insufficientCredits={insufficientCredits} />
      </div>

      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className="node-handle output-handle"
        isConnectable={!!data.hasOutput}
      />
    </div>
  );
});

Image4UltraNode.displayName = 'Image4UltraNode';

export default Image4UltraNode;
